import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { HandbookScene } from '../../api/handbook';
import { C, FONT, classColor, hexToRgba, prefersReducedMotion } from '../../theme/tokens';

/**
 * The reading companion: the chapter you are looking at, drawn as the chain.
 *
 * Every chapter of the handbook explains one mechanic on the same object — a
 * C2 with two statics and whatever K162s are hanging off it. So the page
 * carries one canvas of that object and changes its *state* as you scroll,
 * rather than illustrating chapters separately. Chapter 8 shows the direction
 * lock; chapter 14 drains the mass to collapse; chapter 9 sweeps D-scan and
 * escalates the traffic light. The reader's eye never has to leave the model
 * to find the picture of what they just read.
 *
 * Canvas rather than DOM for the same reason `VoidBackdrop` is: this runs a
 * continuous rAF loop and cross-fades scenes on scroll. Nothing here triggers
 * React work — the scene arrives through a ref.
 *
 * Every dimension scales off the canvas height, because this component is
 * mounted at 300px beside the text on a wide screen and at ~108px in the
 * compact companion on a phone. A fixed node size legible in the first is
 * a collision in the second.
 *
 * Under `prefers-reduced-motion` it paints one settled frame per scene and
 * stops. The information (which nodes, which state) survives; the movement
 * does not.
 */

interface Node {
  x: number; // 0..1 of the padded box
  y: number;
  label: string;
  sub?: string;
  color: string;
  /** `home` is the filled identity node; `ghost` is a not-yet-visible K162. */
  kind: 'home' | 'wh' | 'ghost' | 'hub' | 'gate';
}

interface Link {
  from: number;
  to: number;
  dashed?: boolean;
}

interface Scene {
  nodes: Node[];
  links: Link[];
  /** Node indices the overlay owns, so the base pass leaves them alone. */
  deferred?: number[];
}

const HOME: Node = { x: 0.5, y: 0.5, label: 'HOME', sub: 'C2', color: C.cyan, kind: 'home' };

const SCENES: Record<HandbookScene, Scene> = {
  chain: {
    nodes: [
      HOME,
      { x: 0.12, y: 0.5, label: 'HS', sub: 'B274', color: C.green, kind: 'wh' },
      { x: 0.88, y: 0.5, label: 'C3', sub: 'O477', color: classColor('C3'), kind: 'wh' },
      { x: 0.5, y: 0.12, label: 'K162', sub: 'C5', color: classColor('C5'), kind: 'wh' },
      { x: 0.5, y: 0.88, label: 'K162', sub: 'C4', color: classColor('C4'), kind: 'ghost' },
    ],
    // No link captions: the node subs already carry B274 / O477, and a label
    // at the midpoint of a short link lands on top of the node it describes.
    links: [
      { from: 1, to: 0 },
      { from: 0, to: 2 },
      { from: 3, to: 0 },
      { from: 0, to: 4, dashed: true },
    ],
  },
  spawn: {
    nodes: [
      { ...HOME, x: 0.16, y: 0.36 },
      { x: 0.52, y: 0.36, label: 'O477', sub: 'new sig', color: C.wormhole, kind: 'wh' },
      { x: 0.88, y: 0.36, label: 'C3', color: classColor('C3'), kind: 'wh' },
      // The far side does not exist until the warp creates it, which is the
      // chapter's whole point — so the overlay owns this node, not the base.
      { x: 0.88, y: 0.82, label: 'K162', sub: 'far side', color: C.cyan, kind: 'ghost' },
    ],
    links: [
      { from: 0, to: 1 },
      { from: 1, to: 2, dashed: true },
    ],
    deferred: [3],
  },
  scan: {
    nodes: [
      HOME,
      { x: 0.14, y: 0.2, label: 'ABC', color: C.wormhole, kind: 'ghost' },
      { x: 0.86, y: 0.24, label: 'DEF', color: C.wormhole, kind: 'ghost' },
      { x: 0.16, y: 0.82, label: 'GHI', color: C.wormhole, kind: 'ghost' },
      { x: 0.85, y: 0.78, label: 'JKL', color: C.wormhole, kind: 'ghost' },
    ],
    links: [],
  },
  mass: {
    nodes: [
      { ...HOME, x: 0.22, y: 0.32 },
      { x: 0.78, y: 0.32, label: 'C3', sub: 'O477', color: classColor('C3'), kind: 'wh' },
    ],
    links: [{ from: 0, to: 1 }],
  },
  polarised: {
    nodes: [
      { ...HOME, x: 0.24, y: 0.36 },
      { x: 0.78, y: 0.36, label: 'C3', sub: 'O477', color: classColor('C3'), kind: 'wh' },
    ],
    links: [{ from: 0, to: 1 }],
  },
  threat: {
    nodes: [HOME],
    links: [],
  },
  sites: {
    nodes: [
      { ...HOME, x: 0.14, y: 0.45 },
      { x: 0.64, y: 0.45, label: 'SITE', sub: 'gas', color: C.green, kind: 'wh' },
    ],
    links: [{ from: 0, to: 1, dashed: true }],
  },
  /* Section 3.2, "scan the chain, not the hole": a chain already several hops
   * out, which is what that section argues a member actually needs.
   *
   * It was drawn from an example tree chapter 10 used to carry; 10 now
   * teaches naming instead (see `mapping`), so this scene stands on 3.2 alone
   * and keeps its own illustrative systems. The one-hop `chain` scene stood
   * in here once and drew a home with three neighbours — the opposite of the
   * point. */
  chaindepth: {
    nodes: [
      { ...HOME, x: 0.08, y: 0.5 },
      { x: 0.34, y: 0.14, label: 'HS', sub: 'Jita 14', color: C.green, kind: 'wh' },
      { x: 0.34, y: 0.62, label: 'C3', sub: 'C3-ABC', color: classColor('C3'), kind: 'wh' },
      { x: 0.62, y: 0.38, label: 'K162', sub: '→ HS', color: C.green, kind: 'wh' },
      { x: 0.62, y: 0.9, label: 'C5', sub: 'C5-XYZ', color: classColor('C5'), kind: 'wh' },
      { x: 0.9, y: 0.9, label: 'K162', sub: 'further', color: classColor('C2'), kind: 'ghost' },
    ],
    links: [
      { from: 0, to: 1 },
      { from: 0, to: 2 },
      { from: 2, to: 3 },
      { from: 2, to: 4 },
      { from: 4, to: 5, dashed: true },
    ],
  },
  /* Chapter 13. Not the traffic light — that is chapter 9. This is the
   * connection under observation, plus the things 13.2 says you see through
   * it: a scanner frigate, combat probes, a roller, a forming fleet. */
  holecontrol: {
    nodes: [
      { ...HOME, x: 0.2, y: 0.5 },
      { x: 0.54, y: 0.5, label: 'K162', sub: 'watched', color: C.wormhole, kind: 'wh' },
      { x: 0.86, y: 0.14, label: 'FRIG', sub: 'scanning', color: C.cyan, kind: 'ghost' },
      { x: 0.86, y: 0.38, label: 'PROBES', sub: 'combat', color: C.red, kind: 'ghost' },
      { x: 0.86, y: 0.62, label: 'BS', sub: 'rolling', color: C.gold, kind: 'ghost' },
      { x: 0.86, y: 0.86, label: 'FLEET', sub: 'forming', color: C.red, kind: 'ghost' },
    ],
    links: [
      { from: 0, to: 1 },
      { from: 1, to: 2, dashed: true },
      { from: 1, to: 3, dashed: true },
      { from: 1, to: 4, dashed: true },
      { from: 1, to: 5, dashed: true },
    ],
    // The contacts are revealed one at a time by the overlay, as observations.
    deferred: [2, 3, 4, 5],
  },

  /* Chapter 5's own vocabulary, which it previously borrowed `chain` for:
   * the five terms in its table, each drawn the way the handbook says to
   * treat it — a static that recurs, an inbound K162, an extra wandering
   * connection, and the two states that constrain what you may do. */
  types: {
    nodes: [
      { ...HOME, x: 0.16, y: 0.5 },
      { x: 0.58, y: 0.1, label: 'STATIC', sub: 'B274 · daily', color: C.green, kind: 'wh' },
      { x: 0.58, y: 0.37, label: 'K162', sub: 'inbound', color: C.cyan, kind: 'wh' },
      { x: 0.58, y: 0.64, label: 'WANDER', sub: 'extra', color: C.wormhole, kind: 'wh' },
      { x: 0.58, y: 0.91, label: 'EOL', sub: 'ending', color: C.gold, kind: 'ghost' },
    ],
    links: [
      { from: 0, to: 1 },
      { from: 2, to: 0 },
      { from: 0, to: 3, dashed: true },
      { from: 0, to: 4, dashed: true },
    ],
  },

  /* Chapter 10. Deliberately NOT `chaindepth` (3.2's scene): that one is a
   * finished tree you travel down, this one is the act of building it —
   * systems arriving unknown and being resolved, then *named*.
   *
   * The names are the chapter's own convention, node for node: the example
   * in 10's opening diagram, with 10.1's prefixes and 10.2's suffixes. A
   * pass that labelled these J-codes taught a naming scheme in the prose and
   * showed a different one in the panel beside it. The box carries the
   * segment a member types, the caption under it the full path. */
  mapping: {
    nodes: [
      { ...HOME, x: 0.1, y: 0.5 },
      { x: 0.37, y: 0.5, label: 'C3s', sub: 'h.C3s', color: classColor('C3'), kind: 'ghost' },
      { x: 0.64, y: 0.2, label: 'C4aa', sub: 'h.C3s.C4aa eol', color: C.gold, kind: 'ghost' },
      { x: 0.64, y: 0.8, label: 'C3ba', sub: 'h.C3s.C3ba', color: classColor('C3'), kind: 'ghost' },
      { x: 0.9, y: 0.2, label: 'LSab', sub: 'h.C3s.C4aa.LSab', color: C.gold, kind: 'ghost' },
      { x: 0.9, y: 0.8, label: 'NSbb', sub: 'h.C3s.C3ba.NSbb', color: C.red, kind: 'ghost' },
    ],
    links: [
      { from: 0, to: 1 },
      { from: 1, to: 2 },
      { from: 1, to: 3 },
      { from: 2, to: 4 },
      { from: 3, to: 5 },
    ],
    deferred: [1, 2, 3, 4, 5],
  },

  /* Chapter 14. The mass bar alone did not show the operation: rolling is a
   * ship making controlled transits. The ship is the subject here.
   *
   * 14.2 / 14.3 / 14.4 each get their own scene off the same two nodes — the
   * WHC chart's three procedures differ in what crosses and in what order,
   * which is exactly what a static picture cannot say. See `ROLLS`. */
  rolling: {
    nodes: [
      { ...HOME, x: 0.2, y: 0.3 },
      { x: 0.8, y: 0.3, label: 'C3', sub: 'O477', color: classColor('C3'), kind: 'wh' },
    ],
    links: [{ from: 0, to: 1 }],
  },
  rollnormal: {
    nodes: [
      { ...HOME, x: 0.2, y: 0.3 },
      { x: 0.8, y: 0.3, label: 'C3', sub: 'O477', color: classColor('C3'), kind: 'wh' },
    ],
    links: [{ from: 0, to: 1 }],
  },
  rollshrink: {
    nodes: [
      { ...HOME, x: 0.2, y: 0.3 },
      { x: 0.8, y: 0.3, label: 'C3', sub: 'O477', color: classColor('C3'), kind: 'wh' },
    ],
    links: [{ from: 0, to: 1 }],
  },
  rollrage: {
    nodes: [
      { ...HOME, x: 0.2, y: 0.3 },
      { x: 0.8, y: 0.3, label: 'C3', sub: 'O477', color: classColor('C3'), kind: 'wh' },
    ],
    links: [{ from: 0, to: 1 }],
  },

  /* Chapter 17, as one scene for the whole chapter: you do not know where
   * you are, so you stop, launch probes, resolve what is there, and find the
   * way home. Splitting this across three sub-scenes made the panel change
   * three times inside one short chapter and explained none of them. */
  emergency: {
    nodes: [
      { x: 0.5, y: 0.5, label: 'YOU', sub: 'unknown', color: C.gold, kind: 'home' },
      { x: 0.14, y: 0.22, label: 'SIG', color: C.wormhole, kind: 'ghost' },
      { x: 0.86, y: 0.26, label: 'SIG', color: C.wormhole, kind: 'ghost' },
      { x: 0.18, y: 0.8, label: 'SIG', color: C.wormhole, kind: 'ghost' },
      { x: 0.84, y: 0.78, label: 'SIG', color: C.wormhole, kind: 'ghost' },
    ],
    links: [],
    deferred: [1, 2, 3, 4],
  },

  /* Chapter 18. The six roles around home, each lighting as it reports —
   * the rota and the comms standard are the chapter, so they are the scene. */
  roles: {
    nodes: [
      { ...HOME, x: 0.5, y: 0.5 },
      { x: 0.5, y: 0.08, label: 'SCANNER', color: C.cyan, kind: 'wh' },
      { x: 0.88, y: 0.29, label: 'SCOUT', color: C.cyan, kind: 'wh' },
      { x: 0.88, y: 0.71, label: 'PvE', color: C.green, kind: 'wh' },
      { x: 0.5, y: 0.92, label: 'ROLL', color: C.gold, kind: 'wh' },
      { x: 0.12, y: 0.71, label: 'LOGI', color: C.green, kind: 'wh' },
      { x: 0.12, y: 0.29, label: 'WATCH', color: C.wormhole, kind: 'wh' },
    ],
    links: [
      { from: 1, to: 0 },
      { from: 2, to: 0 },
      { from: 3, to: 0 },
      { from: 4, to: 0 },
      { from: 5, to: 0 },
      { from: 6, to: 0 },
    ],
  },

  /* Chapter 20 is four checklists, so the panel is a checklist: rows ticking
   * off before an op, which is the one thing all four have in common. Drawn
   * entirely by the overlay — there is no chain in this one. */
  checklist: { nodes: [], links: [] },

  /* The closing section. No mechanic to teach and nothing to instruct: the
   * chain at rest, drifting, as a page-end rather than a diagram. */
  closing: {
    nodes: [
      { ...HOME, x: 0.5, y: 0.5 },
      { x: 0.16, y: 0.22, label: '', color: C.cyan, kind: 'gate' },
      { x: 0.84, y: 0.2, label: '', color: C.wormhole, kind: 'gate' },
      { x: 0.12, y: 0.76, label: '', color: C.wormhole, kind: 'gate' },
      { x: 0.86, y: 0.8, label: '', color: C.cyan, kind: 'gate' },
      { x: 0.5, y: 0.12, label: '', color: C.green, kind: 'gate' },
      { x: 0.5, y: 0.9, label: '', color: C.gold, kind: 'gate' },
    ],
    links: [
      { from: 0, to: 1, dashed: true },
      { from: 0, to: 2, dashed: true },
      { from: 0, to: 3, dashed: true },
      { from: 0, to: 4, dashed: true },
      { from: 0, to: 5, dashed: true },
      { from: 0, to: 6, dashed: true },
    ],
  },

  /* Chapter 19. Three levels, earned in order — `scan` was standing in here
   * and said nothing about progression. */
  licence: {
    nodes: [
      { x: 0.18, y: 0.5, label: 'L1', sub: 'explorer', color: C.cyan, kind: 'wh' },
      { x: 0.5, y: 0.5, label: 'L2', sub: 'wh pilot', color: C.green, kind: 'wh' },
      { x: 0.82, y: 0.5, label: 'L3', sub: 'operator', color: C.gold, kind: 'wh' },
    ],
    links: [
      { from: 0, to: 1 },
      { from: 1, to: 2 },
    ],
  },
  /* Chapter 16. A hostile parked on the home hole, and our ship leaving the
   * site grid for a safe rather than warping to it. */
  homedefence: {
    nodes: [
      { ...HOME, x: 0.26, y: 0.34 },
      { x: 0.76, y: 0.34, label: 'HOLE', sub: 'camped', color: C.red, kind: 'wh' },
    ],
    links: [{ from: 0, to: 1, dashed: true }],
  },
  route: {
    nodes: [
      { ...HOME, x: 0.09, y: 0.45 },
      { x: 0.3, y: 0.45, label: 'HS', sub: 'B274', color: C.green, kind: 'wh' },
      { x: 0.47, y: 0.45, label: '', color: C.textDim, kind: 'gate' },
      { x: 0.61, y: 0.45, label: '', color: C.textDim, kind: 'gate' },
      { x: 0.75, y: 0.45, label: '', color: C.textDim, kind: 'gate' },
      { x: 0.91, y: 0.45, label: 'JITA', sub: '14 jumps', color: C.gold, kind: 'hub' },
    ],
    links: [
      { from: 0, to: 1 },
      { from: 1, to: 2 },
      { from: 2, to: 3 },
      { from: 3, to: 4 },
      { from: 4, to: 5 },
    ],
  },
};

/**
 * The WHC static rolling chart's three procedures, as the transits they
 * actually are — the sequences chapter 14 sets out in 14.2, 14.3 and 14.4.
 *
 * `left` is the fraction of a 2 Bkg hole's mass still standing *after* the
 * transit, so the bar carries the chart's own arithmetic rather than a mood:
 * a normal roll's fourth transit lands the hole in shrink and its sixth on
 * 200 Mkg, dead centre of the chart's 180-220 crit window. `ships` is what
 * makes the rage roll a rage roll — four hulls in the hole at once, not one
 * going back and forth.
 */
type Transit = {
  dir: 'out' | 'in';
  /** Written the way the chart writes it; shown verbatim above the bar. */
  note: string;
  left: number;
  ships?: number;
};

const ROLLS: Partial<Record<HandbookScene, Transit[]>> = {
  // A fresh 2 Bkg hole, one roller: battleships until crit, a pseudo-HIC to
  // trim, and the last battleship home through it.
  rollnormal: [
    { dir: 'out', note: 'BS · OUT HOT 300', left: 0.85 },
    { dir: 'in', note: 'BS · IN HOT 300', left: 0.7 },
    { dir: 'out', note: 'BS · OUT HOT 300', left: 0.55 },
    { dir: 'in', note: 'BS · IN HOT 300', left: 0.4 },
    { dir: 'out', note: 'BS · OUT HOT 300', left: 0.25 },
    { dir: 'in', note: 'BS · IN HOT 300', left: 0.1 },
    { dir: 'out', note: 'PSEUDO-HIC · OUT HOT 122', left: 0.04 },
    { dir: 'in', note: 'BS · IN HOT 300', left: 0 },
  ],
  // Picked up on a hole already down to 30%: nothing but the pseudo-HIC, one
  // ship at a time, until it will not take another.
  rollshrink: [
    { dir: 'out', note: 'PSEUDO-HIC · OUT HOT 122', left: 0.24 },
    { dir: 'in', note: 'PSEUDO-HIC · IN HOT 122', left: 0.18 },
    { dir: 'out', note: 'PSEUDO-HIC · OUT HOT 122', left: 0.12 },
    { dir: 'in', note: 'PSEUDO-HIC · IN HOT 122', left: 0.056 },
    { dir: 'out', note: 'PSEUDO-HIC · OUT COLD 22', left: 0.045 },
    { dir: 'in', note: 'PSEUDO-HIC · IN HOT 122', left: 0 },
  ],
  // A brand-new hole and no patience: 1000 Mkg out in one wave, which is the
  // shrink check the chart makes, then 700 back, then the last one home.
  rollrage: [
    { dir: 'out', note: '4x BS · OUT  COLD 200 · COLD 200 · HOT 300 · HOT 300', left: 0.5, ships: 4 },
    { dir: 'in', note: '3x BS · IN  COLD 200 · COLD 200 · HOT 300', left: 0.15, ships: 3 },
    { dir: 'in', note: 'BS · IN HOT 300', left: 0 },
  ],
};

/**
 * What the hole has left when the procedure is picked up. A normal or rage
 * roll starts on a hole nobody has spent yet; a shrink roll is by definition
 * a hole already down past half, and starting its bar at full would show the
 * opposite of what 14.3 is about.
 */
const ROLL_START: Partial<Record<HandbookScene, number>> = { rollshrink: 0.3 };

/** Scene loop length, so every animation restarts on a clean beat. */
const PERIOD = 7200;
/** How long an outgoing scene has to leave. Short: this fires on scroll, and
 * a reader moving quickly must never see two chains stacked on each other. */
/** How long the retune sweep takes to cross the panel. Long enough to read as
 * a deliberate switch, short enough that a reader scrolling steadily is never
 * waiting on it. */
const SWITCH_MS = 460;

const ChainCanvas: React.FC<{ scene: HandbookScene; height: number }> = ({ scene, height }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // The scene arrives by ref so a scroll-driven change never re-renders React
  // or restarts the rAF loop; only the cross-fade below reads it.
  const sceneRef = useRef<HandbookScene>(scene);
  /** Set by the paint effect. Reduced motion runs no loop, so a scene change
   * has to ask for its one frame explicitly. */
  const repaintRef = useRef<(() => void) | null>(null);
  /** Drives the element-level dip that marks a switch. */
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    if (sceneRef.current === scene) return;
    sceneRef.current = scene;
    // Reflected onto the element so the scene the canvas is actually painting
    // is inspectable from the DOM — pixels are not. Deliberately NOT
    // `data-scene`: that is the page's anchor attribute, and naming this the
    // same made the canvas match the anchor query and feed its own scene back
    // in as input.
    if (canvasRef.current) canvasRef.current.dataset.canvasScene = scene;
    if (prefersReducedMotion()) repaintRef.current?.();
    // Cosmetic only — the pixels underneath are already the new scene.
    setSwitching(true);
    const t = window.setTimeout(() => setSwitching(false), SWITCH_MS / 2);
    return () => window.clearTimeout(t);
    // The debounce that used to live here now sits in the page, so the
    // caption and the canvas are driven by the same settled value. Holding it
    // here made the canvas lag its own caption whenever the page's raw scene
    // was still changing — the panel said "wh pilot" under a caption that had
    // already moved on to the next chapter.
  }, [scene]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let w = 0;
    let h = 0;
    let k = 1; // everything scales off height: 300px tall is the reference
    let padX = 0;
    let padY = 0;
    let frame = 0;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = Math.max(1, Math.round(w * dpr));
      canvas.height = Math.max(1, Math.round(h * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      k = Math.max(0.6, Math.min(1.35, h / 300));
      padX = 30 * k;
      padY = 16 * k;
    };

    /* Scenes are authored in a 0..1 box, and that box is centred inside the
     * canvas rather than stretched to fill it. The panel is 150px tall on a
     * phone and 430 on a wide monitor; stretching would smear a scene into a
     * tall thin diagram at one size and squash it at the other. Capping the
     * box's height against its own width keeps every scene the same shape and
     * simply centres it in whatever room there is. */
    const boxW = () => w - padX * 2;
    const boxH = () => Math.min(h - 34 * k, boxW() * 0.62);
    const boxTop = () => Math.max(padY, (h - 26 * k - boxH()) / 2);

    const px = (x: number) => padX + x * boxW();
    const py = (y: number) => boxTop() + y * boxH();

    /* Nodes shrink to fit their neighbours rather than being sized purely off
     * the canvas. `route` puts six nodes in one row, so at a 400px panel the
     * HOME and HS boxes ran into each other — reported on chapter 2.1. Rather
     * than hand-tuning that scene, every scene measures the tightest pair
     * sharing a row and scales its boxes to fit, which also holds for any
     * scene added later and for every panel width in between. */
    let nodeScale = 1;

    const baseHalfW = (n: Node) => (n.kind === 'gate' ? 5 : n.kind === 'home' ? 28 : 23) * k;

    const fitNodes = (s: Scene) => {
      const bw = boxW();
      const bh = boxH();
      let worst = 1;
      for (let i = 0; i < s.nodes.length; i += 1) {
        for (let j = i + 1; j < s.nodes.length; j += 1) {
          const a = s.nodes[i];
          const b = s.nodes[j];
          // Only nodes on the same row can collide horizontally.
          if (Math.abs(a.y - b.y) * bh > 26 * k) continue;
          const gap = Math.abs(a.x - b.x) * bw;
          const needed = baseHalfW(a) + baseHalfW(b) + 7 * k;
          if (gap < needed) worst = Math.min(worst, gap / needed);
        }
      }
      // A floor: past this the labels stop being readable, and a scene that
      // tight wants fewer nodes, not smaller ones.
      nodeScale = Math.max(0.62, worst);
    };

    const nodeW = (n: Node) => (n.kind === 'home' ? 56 : 46) * k * nodeScale;
    const nodeH = () => 20 * k * nodeScale;

    /** The radius that actually clears a node's box, corner included. Every
     * ring, sweep or orbit drawn from a node's centre starts outside this —
     * the polarisation timer was drawn at a flat `20 * k`, which is *inside*
     * HOME's 28px half-width, so the ring cut straight through the box. */
    const nodeClear = (n: Node) => Math.hypot(nodeW(n) / 2, nodeH() / 2) + 9 * k;

    const label = (text: string, x: number, y: number, color: string, size = 9.5, mono = true) => {
      ctx.font = `${mono ? '' : '600 '}${size * k}px ${mono ? FONT.mono : FONT.sub}`;
      ctx.fillStyle = color;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, x, y);
    };

    /** The scene's own state readout, pinned to the bottom edge so it never
     * lands on a node however short the canvas is. */
    /** Every scene puts its readout on the same baseline, so during a
     * cross-fade two of them land on the exact same pixels and interleave
     * into unreadable mush ("1 / 4 SIGNA-STABLE-RESOLVED", caught in review).
     * Only the incoming scene gets to label itself; the outgoing one drops
     * its line immediately, which is no loss — half-faded text is not
     * readable anyway. */
    let labelling = true;
    const statusLine = (text: string, color: string) => {
      if (!labelling) return;
      label(text, w / 2, h - 8 * k, color, 8.5, false);
    };

    const roundRect = (x: number, y: number, rw: number, rh: number, r: number) => {
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.arcTo(x + rw, y, x + rw, y + rh, r);
      ctx.arcTo(x + rw, y + rh, x, y + rh, r);
      ctx.arcTo(x, y + rh, x, y, r);
      ctx.arcTo(x, y, x + rw, y, r);
      ctx.closePath();
    };

    const drawLink = (a: Node, b: Node, l: Link, alpha: number) => {
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = hexToRgba(C.wormhole, l.dashed ? 0.3 : 0.5);
      ctx.lineWidth = 1;
      ctx.setLineDash(l.dashed ? [3, 4] : []);
      ctx.beginPath();
      ctx.moveTo(px(a.x), py(a.y));
      ctx.lineTo(px(b.x), py(b.y));
      ctx.stroke();
      ctx.restore();
    };

    const drawNode = (n: Node, alpha: number, t: number) => {
      const x = px(n.x);
      const y = py(n.y);
      ctx.save();
      ctx.globalAlpha = alpha;

      if (n.kind === 'gate') {
        ctx.strokeStyle = hexToRgba(C.text, 0.4);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(x, y, 3.5 * k, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
        return;
      }

      const bw = nodeW(n);
      const bh = nodeH();
      roundRect(x - bw / 2, y - bh / 2, bw, bh, 5 * k);

      if (n.kind === 'home') {
        ctx.fillStyle = hexToRgba(n.color, 0.16);
        ctx.fill();
        ctx.shadowColor = hexToRgba(n.color, 0.5);
        ctx.shadowBlur = 12;
      } else {
        // Opaque: a node sitting on a link must cover it, or the line reads
        // as passing straight through the box.
        ctx.fillStyle = C.surface;
        ctx.fill();
      }
      ctx.strokeStyle = hexToRgba(n.color, n.kind === 'ghost' ? 0.35 : 0.75);
      ctx.lineWidth = 1;
      ctx.setLineDash(n.kind === 'ghost' ? [3, 3] : []);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.shadowBlur = 0;

      // Type rides the same scale as the box, or a shrunk node's label spills
      // straight back out of it.
      label(n.label, x, y, n.kind === 'ghost' ? hexToRgba(n.color, 0.7) : n.color, 9.5 * nodeScale);
      if (n.sub) label(n.sub, x, y + bh / 2 + 7 * k, hexToRgba(C.text, 0.55), 8 * nodeScale, false);

      // A home node breathes very slightly, so a still page is never dead.
      if (n.kind === 'home') {
        ctx.globalAlpha = alpha * (0.4 + 0.25 * Math.sin(t * 0.0016));
        ctx.strokeStyle = hexToRgba(n.color, 0.5);
        roundRect(x - bw / 2 - 3 * k, y - bh / 2 - 3 * k, bw + 6 * k, bh + 6 * k, 7 * k);
        ctx.stroke();
      }
      ctx.restore();
    };

    /** A dot running along a link — the one motion shared by several scenes. */
    const traveller = (a: Node, b: Node, p: number, color: string, alpha = 1) => {
      const x = px(a.x) + (px(b.x) - px(a.x)) * p;
      const y = py(a.y) + (py(b.y) - py(a.y)) * p;
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = color;
      ctx.shadowColor = hexToRgba(color, 0.9);
      ctx.shadowBlur = 8;
      ctx.beginPath();
      ctx.arc(x, y, 2.6 * k, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    };

    /** Everything a scene adds on top of its own nodes and links. `q` runs
     * 0..1 once per PERIOD; `alpha` carries the cross-fade. */
    const overlay = (name: HandbookScene, q: number, alpha: number, t: number) => {
      const s = SCENES[name];
      ctx.save();
      ctx.globalAlpha = alpha;

      if (name === 'chain') {
        // Traffic outward from home, one leg at a time — the chain is a thing
        // you move through, not a diagram.
        const legs: [number, number][] = [
          [0, 1],
          [0, 2],
          [0, 3],
        ];
        const leg = legs[Math.floor(q * 3) % 3];
        const p = (q * 3) % 1;
        traveller(s.nodes[leg[0]], s.nodes[leg[1]], p, C.cyan, alpha * (1 - Math.abs(p - 0.5) * 0.6));
        statusLine('HOME AND ITS CONNECTIONS', hexToRgba(C.text, 0.6));
      }

      if (name === 'spawn') {
        // Warp out to the new sig, then the far side comes into existence:
        // dashed and dim, then solid — chapter 4's whole point.
        if (q < 0.4) traveller(s.nodes[0], s.nodes[1], Math.min(1, q / 0.35), C.cyan, alpha);
        const born = q > 0.4 ? Math.min(1, (q - 0.4) / 0.25) : 0;
        if (born > 0) drawNode({ ...s.nodes[3], kind: q > 0.72 ? 'wh' : 'ghost' }, alpha * born, t);
        if (q > 0.72) traveller(s.nodes[1], s.nodes[2], Math.min(1, (q - 0.72) / 0.2), C.cyan, alpha);
        statusLine(
          q < 0.4 ? 'WARP' : q < 0.72 ? 'K162 CREATED — INVISIBLE' : 'JUMP — NOW VISIBLE',
          q < 0.72 ? hexToRgba(C.text, 0.7) : C.cyan,
        );
      }

      if (name === 'scan') {
        // Probe rings contract onto home, and the four unknowns resolve one
        // after another as the sweep passes them.
        const cx = px(0.5);
        const cy = py(0.5);
        for (let i = 0; i < 3; i += 1) {
          const rp = (q + i / 3) % 1;
          ctx.globalAlpha = alpha * (1 - rp) * 0.5;
          ctx.strokeStyle = hexToRgba(C.cyan, 0.8);
          ctx.lineWidth = 1;
          ctx.beginPath();
          // Probe rings start outside HOME, not through it.
          ctx.arc(cx, cy, nodeClear(s.nodes[0]) + rp * Math.min(w, h) * 0.42, 0, Math.PI * 2);
          ctx.stroke();
        }
        ctx.globalAlpha = alpha;
        const names = ['', 'B274', 'K162', 'O477', 'DATA'];
        for (let i = 1; i < s.nodes.length; i += 1) {
          if (q <= i / 5) continue;
          drawNode({ ...s.nodes[i], kind: 'wh', label: names[i], color: C.cyan }, alpha, t);
        }
        statusLine(`${Math.min(4, Math.floor(q * 5))} / 4 SIGNATURES RESOLVED`, hexToRgba(C.text, 0.7));
      }

      if (name === 'mass') {
        // Total mass draining to collapse, then the static respawns — the
        // model chapters 7 and 14 both describe.
        const remaining = 1 - (q < 0.86 ? q / 0.86 : 1);
        const x0 = px(0.14);
        const x1 = px(0.86);
        const y = py(0.82);
        const barW = x1 - x0;
        const barH = 7 * k;
        ctx.globalAlpha = alpha;
        ctx.fillStyle = hexToRgba(C.text, 0.1);
        roundRect(x0, y, barW, barH, barH / 2);
        ctx.fill();
        const tone = remaining > 0.5 ? C.green : remaining > 0.1 ? C.gold : C.red;
        if (remaining > 0.001) {
          ctx.fillStyle = tone;
          roundRect(x0, y, Math.max(barH, barW * remaining), barH, barH / 2);
          ctx.fill();
        }
        statusLine(
          remaining > 0.5
            ? 'STABLE'
            : remaining > 0.1
              ? 'REDUCED'
              : remaining > 0.001
                ? 'CRITICAL'
                : 'COLLAPSED — NEW STATIC',
          tone,
        );
        // Rollers crossing, each transit taking a bite out of the bar.
        if (remaining > 0.001) {
          const even = Math.floor(q * 6) % 2 === 0;
          traveller(s.nodes[even ? 0 : 1], s.nodes[even ? 1 : 0], (q * 6) % 1, C.wormhole, alpha);
        }
      }

      if (name === 'polarised') {
        // A jump that is refused: the dot reaches the midpoint and comes back,
        // while the five-minute timer runs down on home.
        const blocked = q > 0.25;
        const p = (q % 0.25) / 0.25;
        traveller(s.nodes[0], s.nodes[1], blocked ? p * 0.45 : p, blocked ? C.red : C.cyan, alpha);
        const cx = px(s.nodes[0].x);
        const cy = py(s.nodes[0].y);
        if (blocked) {
          const mx = (cx + px(s.nodes[1].x)) / 2;
          const d = 5 * k;
          ctx.globalAlpha = alpha * 0.85;
          ctx.strokeStyle = C.red;
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(mx - d, cy - d);
          ctx.lineTo(mx + d, cy + d);
          ctx.moveTo(mx + d, cy - d);
          ctx.lineTo(mx - d, cy + d);
          ctx.stroke();
        }
        const left = 1 - q;
        // Clears HOME's box rather than cutting through it.
        const r = nodeClear(s.nodes[0]);
        ctx.globalAlpha = alpha;
        ctx.lineWidth = 2;
        ctx.strokeStyle = hexToRgba(C.text, 0.14);
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.stroke();
        ctx.strokeStyle = blocked ? C.red : C.green;
        ctx.beginPath();
        ctx.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * left);
        ctx.stroke();
        const secs = Math.max(0, Math.round(300 * left));
        label(
          `${String(Math.floor(secs / 60)).padStart(2, '0')}:${String(secs % 60).padStart(2, '0')}`,
          cx,
          cy + r + 12 * k,
          blocked ? C.red : C.green,
          10,
        );
        statusLine(
          blocked ? 'POLARISED — SAME DIRECTION BLOCKED' : 'TRANSIT CLEAR',
          hexToRgba(blocked ? C.red : C.green, 0.9),
        );
      }

      if (name === 'threat') {
        // The traffic light, driven by what the sweep finds.
        const cx = px(0.5);
        const cy = py(0.46);
        // Clamped: `h - 40 * k` goes negative on a zero-height canvas, and a
        // negative radius makes createRadialGradient throw.
        const rmax = Math.max(1, Math.min(w * 0.42, (h - 40 * k) * 0.5));
        const step = q < 0.3 ? 0 : q < 0.55 ? 1 : q < 0.82 ? 2 : 3;
        const tone = [C.green, C.gold, C.red, C.red][step];
        const angle = q * Math.PI * 8;
        const g = ctx.createRadialGradient(cx, cy, 6, cx, cy, rmax);
        g.addColorStop(0, hexToRgba(tone, 0.22));
        g.addColorStop(1, hexToRgba(tone, 0));
        ctx.globalAlpha = alpha;
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(cx, cy, rmax, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = hexToRgba(tone, 0.75);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx + Math.cos(angle) * rmax, cy + Math.sin(angle) * rmax);
        ctx.stroke();
        const contacts: [number, number, string][] = [
          [2.3, 0.78, 'SCOUT'],
          [4.2, 0.86, 'PROBES'],
          [5.7, 0.64, 'HOSTILES'],
        ];
        contacts.slice(0, step).forEach(([a, rr, txt]) => {
          const x = cx + Math.cos(a) * rmax * rr;
          const y = cy + Math.sin(a) * rmax * rr;
          ctx.fillStyle = tone;
          ctx.beginPath();
          ctx.arc(x, y, 2.6 * k, 0, Math.PI * 2);
          ctx.fill();
          label(txt, x, y - 10 * k, hexToRgba(tone, 0.9), 8, false);
        });
        statusLine(['GREEN', 'YELLOW', 'RED', 'BLACK'][step], tone);
      }

      if (name === 'sites') {
        // A site grid that looks empty, then does not: the delayed sleeper
        // spawn chapter 12 warns about.
        const cx = px(0.64);
        const cy = py(0.45);
        const rr = Math.min(30 * k + 6, (h - 46 * k) * 0.32);
        for (let i = 0; i < 6; i += 1) {
          const a = (i / 6) * Math.PI * 2 + q * 0.4;
          ctx.globalAlpha = alpha * 0.7;
          ctx.strokeStyle = hexToRgba(C.green, 0.7);
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.arc(cx + Math.cos(a) * rr, cy + Math.sin(a) * rr * 0.8, 3 * k, 0, Math.PI * 2);
          ctx.stroke();
        }
        const spawn = q > 0.55 ? Math.min(1, (q - 0.55) / 0.2) : 0;
        if (spawn > 0) {
          for (let i = 0; i < 4; i += 1) {
            const a = (i / 4) * Math.PI * 2 + 0.6;
            ctx.globalAlpha = alpha * spawn;
            ctx.fillStyle = C.red;
            ctx.beginPath();
            ctx.arc(cx + Math.cos(a) * rr * 1.6, cy + Math.sin(a) * rr * 1.25, 3 * k, 0, Math.PI * 2);
            ctx.fill();
          }
        }
        ctx.globalAlpha = alpha;
        statusLine(
          spawn > 0 ? 'SLEEPER RESPONSE ON SITE' : 'GRID READS EMPTY — TIMER RUNNING',
          hexToRgba(spawn > 0 ? C.red : C.text, 0.8),
        );
      }

      if (name === 'chaindepth') {
        /* 3.2's whole claim is that one hole tells you nothing and the chain
         * does. So the scout walks the deep branch and *keeps* what it has
         * been through: each leg it clears stays lit behind it, the branches
         * it has not taken sit dim, and the readout counts hops rather than
         * systems. Next to chapter 3's own `chain` scene — traffic bouncing
         * one hop off home — the difference is the chapter's argument. */
        const legs: [number, number][] = [
          [0, 2],
          [2, 4],
          [4, 5],
        ];
        const cleared = Math.min(legs.length, Math.floor(q * (legs.length + 1)));
        const p = (q * (legs.length + 1)) % 1;

        // Everything not on the deep branch recedes: this is about one route
        // followed all the way out, not about the whole star at once.
        const onBranch = new Set([0, 2, 4, 5]);
        s.nodes.forEach((n, i) => {
          if (onBranch.has(i)) return;
          ctx.globalAlpha = alpha * 0.35;
          drawNode(n, alpha * 0.35, t);
        });
        ctx.globalAlpha = alpha;

        // The trail: legs already walked stay lit, so depth accumulates on
        // screen instead of a single dot wandering.
        legs.forEach(([a, b], i) => {
          if (i >= cleared) return;
          ctx.save();
          ctx.globalAlpha = alpha * 0.9;
          ctx.strokeStyle = hexToRgba(C.cyan, 0.75);
          ctx.lineWidth = 1.6;
          ctx.shadowColor = hexToRgba(C.cyan, 0.5);
          ctx.shadowBlur = 6;
          ctx.beginPath();
          ctx.moveTo(px(s.nodes[a].x), py(s.nodes[a].y));
          ctx.lineTo(px(s.nodes[b].x), py(s.nodes[b].y));
          ctx.stroke();
          ctx.restore();
          drawNode({ ...s.nodes[b], kind: 'wh', color: C.cyan }, alpha, t);
        });

        if (cleared < legs.length) {
          const [a, b] = legs[cleared];
          traveller(s.nodes[a], s.nodes[b], p, C.cyan, alpha);
        }
        statusLine(
          cleared === 0
            ? 'ONE HOLE TELLS YOU LITTLE'
            : `${cleared} HOP${cleared > 1 ? 'S' : ''} OUT — AND WHAT IS BEHIND IT`,
          cleared === 0 ? hexToRgba(C.text, 0.7) : hexToRgba(C.cyan, 0.9),
        );
      }

      if (name === 'types') {
        // One term at a time, with the handbook's own treatment for it. The
        // chapter is a vocabulary, so the scene reads it out.
        const terms = [
          { i: 1, txt: 'STATIC · FIND IT DAILY', tone: C.green },
          { i: 2, txt: 'K162 · INBOUND ACCESS', tone: C.cyan },
          { i: 3, txt: 'WANDERING · SCAN IT TOO', tone: C.wormhole },
          { i: 4, txt: 'EOL · NOT FOR CRITICAL PLANS', tone: C.gold },
        ];
        const cur = terms[Math.floor(q * terms.length) % terms.length];
        const n = s.nodes[cur.i];
        const x = px(n.x);
        const y = py(n.y);
        ctx.globalAlpha = alpha * (0.55 + 0.45 * Math.sin(q * Math.PI * terms.length * 2));
        ctx.strokeStyle = cur.tone;
        ctx.lineWidth = 1.4;
        roundRect(x - nodeW(n) / 2 - 4 * k, y - nodeH() / 2 - 4 * k, nodeW(n) + 8 * k, nodeH() + 8 * k, 7 * k);
        ctx.stroke();
        ctx.globalAlpha = alpha;
        // K162 is the one that points inward; the rest run outward from home.
        const inbound = cur.i === 2;
        traveller(
          inbound ? n : s.nodes[0],
          inbound ? s.nodes[0] : n,
          (q * terms.length) % 1,
          cur.tone,
          alpha,
        );
        statusLine(cur.txt, cur.tone);
      }

      if (name === 'mapping') {
        // Unknowns resolving into named systems, outward from home. The name
        // is the point: an unnamed hop is not mapped, it is only visited —
        // so each node arrives carrying the path a member would type.
        const order = [1, 2, 3, 4, 5];
        const done = Math.min(order.length, Math.floor(q * (order.length + 1)));
        const shown = new Set([0, ...order.slice(0, done)]);
        // Links arrive with the node they lead to, so the map grows outward
        // rather than showing edges into empty space.
        s.links.forEach((l) => {
          if (!shown.has(l.from) || !shown.has(l.to)) return;
          drawLink(s.nodes[l.from], s.nodes[l.to], l, alpha * 0.9);
        });
        order.forEach((idx, i) => {
          if (i >= done) return;
          // Resolved: the node keeps its own colour (the class, or gold for
          // the EOL branch) — that is what the suffix in its name says.
          drawNode({ ...s.nodes[idx], kind: 'wh' }, alpha, t);
        });
        // The one currently being resolved gets the scanner ring.
        if (done < order.length) {
          const idx = order[done];
          const n = s.nodes[idx];
          // The system being resolved: its link is drawn ahead of it, and a
          // scout runs out along that link — a chain is not discovered by
          // systems appearing, it is discovered by someone going there.
          const via = s.links.find((l) => l.to === idx);
          const p = (q * (order.length + 1)) % 1;
          if (via) {
            drawLink(s.nodes[via.from], n, { ...via, dashed: true }, alpha * 0.45);
            traveller(s.nodes[via.from], n, p, C.cyan, alpha);
          }
          // No `sub` while it is still a question mark: the caption is the
          // name, and the name is exactly what this hop does not have yet.
          drawNode(
            { ...n, label: '?', sub: undefined, color: hexToRgba(C.wormhole, 0.7) },
            alpha * 0.7,
            t,
          );
          // A tight pulse on the node, sized to the node rather than to the
          // canvas, so it reads as "this one is being scanned" rather than as
          // a stray circle on the diagram.
          const pulse = (q * 3) % 1;
          ctx.globalAlpha = alpha * (1 - pulse) * 0.7;
          ctx.strokeStyle = hexToRgba(C.cyan, 0.8);
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.arc(px(n.x), py(n.y), Math.max(2, nodeClear(n) * (0.5 + pulse * 0.6)), 0, Math.PI * 2);
          ctx.stroke();
          ctx.globalAlpha = alpha;
        }
        // The readout names the hop in the chapter's own format, so the panel
        // and 10.1/10.2 are speaking the same language.
        const naming = s.nodes[order[Math.min(done, order.length - 1)]].sub;
        statusLine(
          `${naming} · ${done} / ${order.length} MAPPED`,
          hexToRgba(C.text, 0.75),
        );
      }

      if (name === 'rolling') {
        // The ship is the subject: it crosses, comes back, crosses again, and
        // the hole's mass drops with every transit until it collapses.
        const transits = 5;
        const step = Math.min(transits, Math.floor(q * (transits + 1)));
        const collapsed = step >= transits;
        const remaining = Math.max(0, 1 - step / transits);

        const x0 = px(0.14);
        const x1 = px(0.86);
        const barY = py(0.78);
        const barW = x1 - x0;
        const barH = 7 * k;
        ctx.globalAlpha = alpha;
        ctx.fillStyle = hexToRgba(C.text, 0.1);
        roundRect(x0, barY, barW, barH, barH / 2);
        ctx.fill();
        const tone = remaining > 0.5 ? C.green : remaining > 0.2 ? C.gold : C.red;
        if (remaining > 0.001) {
          ctx.fillStyle = tone;
          roundRect(x0, barY, Math.max(barH, barW * remaining), barH, barH / 2);
          ctx.fill();
        }

        if (!collapsed) {
          // Outbound on even transits, back on odd — polarisation is why a
          // roller alternates rather than repeating one direction.
          const outbound = step % 2 === 0;
          const p = (q * (transits + 1)) % 1;
          const a = outbound ? s.nodes[0] : s.nodes[1];
          const b = outbound ? s.nodes[1] : s.nodes[0];
          const sx = px(a.x) + (px(b.x) - px(a.x)) * p;
          const sy = py(a.y) + (py(b.y) - py(a.y)) * p;
          // A ship, not a dot: a small hull pointing the way it travels.
          ctx.save();
          ctx.globalAlpha = alpha;
          ctx.translate(sx, sy);
          ctx.rotate(outbound ? 0 : Math.PI);
          ctx.fillStyle = C.cyan;
          ctx.shadowColor = hexToRgba(C.cyan, 0.9);
          ctx.shadowBlur = 9;
          ctx.beginPath();
          ctx.moveTo(7 * k, 0);
          ctx.lineTo(-5 * k, 4.2 * k);
          ctx.lineTo(-2.5 * k, 0);
          ctx.lineTo(-5 * k, -4.2 * k);
          ctx.closePath();
          ctx.fill();
          ctx.restore();
          label(
            `TRANSIT ${step + 1} / ${transits}  ${outbound ? 'OUT' : 'IN'}`,
            px(0.5),
            barY - 14 * k,
            hexToRgba(C.text, 0.7),
            8.5,
            false,
          );
          statusLine(
            remaining > 0.5 ? 'STABLE — KEEP ROLLING' : remaining > 0.2 ? 'REDUCED' : 'CRITICAL — LAST TRANSITS',
            tone,
          );
        } else {
          const cx = (px(s.nodes[0].x) + px(s.nodes[1].x)) / 2;
          const cy = py(s.nodes[0].y);
          const burst = ((q * (transits + 1)) % 1) * 26 * k;
          ctx.globalAlpha = alpha * Math.max(0, 1 - burst / (26 * k));
          ctx.strokeStyle = C.red;
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.arc(cx, cy, Math.max(1, burst), 0, Math.PI * 2);
          ctx.stroke();
          ctx.globalAlpha = alpha;
          statusLine('COLLAPSED — SCAN THE NEW STATIC', C.red);
        }
      }

      const roll = ROLLS[name];
      if (roll) {
        // One overlay for all three procedures: they differ in what crosses,
        // how many at a time and how far the mass drops, and that is exactly
        // what the table above holds. The chapter's states are the chart's:
        // stable, shrink under 50%, crit under 10%, then gone.
        const step = Math.min(roll.length, Math.floor(q * (roll.length + 1)));
        const collapsed = step >= roll.length;
        const cur = roll[Math.min(step, roll.length - 1)];
        const before = step === 0 ? (ROLL_START[name] ?? 1) : roll[step - 1].left;
        const p = (q * (roll.length + 1)) % 1;
        // Mass drains across the transit rather than snapping at the end of
        // it — the bar and the hull are the same event.
        const remaining = collapsed ? 0 : before + (cur.left - before) * p;

        const x0 = px(0.14);
        const x1 = px(0.86);
        const barY = py(0.78);
        const barW = x1 - x0;
        const barH = 7 * k;
        ctx.globalAlpha = alpha;
        ctx.fillStyle = hexToRgba(C.text, 0.1);
        roundRect(x0, barY, barW, barH, barH / 2);
        ctx.fill();
        const tone = remaining > 0.5 ? C.green : remaining > 0.1 ? C.gold : C.red;
        if (remaining > 0.001) {
          ctx.fillStyle = tone;
          roundRect(x0, barY, Math.max(barH, barW * remaining), barH, barH / 2);
          ctx.fill();
        }

        if (!collapsed) {
          const outbound = cur.dir === 'out';
          const a = outbound ? s.nodes[0] : s.nodes[1];
          const b = outbound ? s.nodes[1] : s.nodes[0];
          const ships = cur.ships ?? 1;
          for (let i = 0; i < ships; i++) {
            // Spread a wave across the link rather than stacking it on one
            // pixel, and stagger it slightly so four hulls read as four.
            const lane = (i - (ships - 1) / 2) * 7 * k;
            const pp = Math.max(0, Math.min(1, p - i * 0.04));
            const sx = px(a.x) + (px(b.x) - px(a.x)) * pp;
            const sy = py(a.y) + (py(b.y) - py(a.y)) * pp + lane;
            ctx.save();
            ctx.globalAlpha = alpha;
            ctx.translate(sx, sy);
            ctx.rotate(outbound ? 0 : Math.PI);
            ctx.fillStyle = C.cyan;
            ctx.shadowColor = hexToRgba(C.cyan, 0.9);
            ctx.shadowBlur = 9;
            ctx.beginPath();
            ctx.moveTo(7 * k, 0);
            ctx.lineTo(-5 * k, 4.2 * k);
            ctx.lineTo(-2.5 * k, 0);
            ctx.lineTo(-5 * k, -4.2 * k);
            ctx.closePath();
            ctx.fill();
            ctx.restore();
          }
          label(cur.note, px(0.5), barY - 14 * k, hexToRgba(C.text, 0.75), 8, false);
          statusLine(
            remaining > 0.5
              ? `STABLE · ${step + 1} / ${roll.length}`
              : remaining > 0.1
                ? `SHRINK · ${step + 1} / ${roll.length}`
                : `CRIT · ${step + 1} / ${roll.length}`,
            tone,
          );
        } else {
          const cx = (px(s.nodes[0].x) + px(s.nodes[1].x)) / 2;
          const cy = py(s.nodes[0].y);
          const burst = ((q * (roll.length + 1)) % 1) * 26 * k;
          ctx.globalAlpha = alpha * Math.max(0, 1 - burst / (26 * k));
          ctx.strokeStyle = C.red;
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.arc(cx, cy, Math.max(1, burst), 0, Math.PI * 2);
          ctx.stroke();
          ctx.globalAlpha = alpha;
          statusLine('COLLAPSED — CLEAN UP THE BOOKMARKS', C.red);
        }
      }

      if (name === 'emergency') {
        // Stop, probe, resolve, find the way home. One arc for the whole
        // chapter, so the panel is not changing three times in half a screen.
        const cx = px(0.5);
        const cy = py(0.5);
        const phase = q < 0.18 ? 0 : q < 0.72 ? 1 : 2;

        if (phase === 0) {
          // Do not keep flying: the ship holds still and the panel says so.
          ctx.globalAlpha = alpha * (0.5 + 0.5 * Math.sin(t * 0.006));
          ctx.strokeStyle = C.gold;
          ctx.lineWidth = 1.4;
          ctx.beginPath();
          ctx.arc(cx, cy, nodeClear(s.nodes[0]), 0, Math.PI * 2);
          ctx.stroke();
          ctx.globalAlpha = alpha;
          statusLine('STOP — DO NOT KEEP FLYING', C.gold);
        } else {
          // Probes out: rings expand, signatures resolve one by one.
          for (let i = 0; i < 3; i += 1) {
            const rp = ((q * 2 + i / 3) % 1);
            ctx.globalAlpha = alpha * (1 - rp) * 0.5;
            ctx.strokeStyle = hexToRgba(C.cyan, 0.8);
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.arc(cx, cy, nodeClear(s.nodes[0]) + rp * Math.min(w, h) * 0.4, 0, Math.PI * 2);
            ctx.stroke();
          }
          ctx.globalAlpha = alpha;
          const solved = Math.min(4, Math.floor(((q - 0.18) / 0.54) * 5));
          for (let i = 1; i <= solved; i += 1) {
            const home = phase === 2 && i === 3;
            drawNode(
              {
                ...s.nodes[i],
                kind: 'wh',
                label: home ? 'HOME' : ['', 'C3', 'K162', 'HOME', 'C5'][i],
                sub: home ? 'bookmark' : undefined,
                color: home ? C.green : C.cyan,
              },
              alpha,
              t,
            );
          }
          if (phase === 2) {
            traveller(s.nodes[0], s.nodes[3], ((q - 0.72) / 0.28), C.green, alpha);
            statusLine('WAY HOME FOUND — TELL THE CORP', C.green);
          } else {
            statusLine(`PROBES OUT — ${solved} / 4 RESOLVED`, hexToRgba(C.cyan, 0.9));
          }
        }
      }

      if (name === 'roles') {
        // Each role reports in turn, in the comms format the chapter defines.
        const calls = [
          { i: 1, txt: '[NEW WH] Home → K162 C4 | stable', tone: C.cyan },
          { i: 2, txt: '[HS] B274 → 9 jumps Jita | clear', tone: C.cyan },
          { i: 3, txt: '[SITE] Core Garrison | fleet up', tone: C.green },
          { i: 4, txt: '[ROLL] O477 | stage ready | green', tone: C.gold },
          { i: 5, txt: '[LOGI] freight staged | 18:22', tone: C.green },
          { i: 6, txt: '[HOSTILE] C3 side | 2 ships', tone: C.red },
        ];
        const cur = calls[Math.floor(q * calls.length) % calls.length];
        const n = s.nodes[cur.i];
        // The reporting role lights, and its call runs in to home.
        ctx.globalAlpha = alpha * (0.5 + 0.5 * Math.sin(q * Math.PI * calls.length * 2));
        ctx.strokeStyle = cur.tone;
        ctx.lineWidth = 1.4;
        roundRect(px(n.x) - nodeW(n) / 2 - 4 * k, py(n.y) - nodeH() / 2 - 4 * k, nodeW(n) + 8 * k, nodeH() + 8 * k, 7 * k);
        ctx.stroke();
        ctx.globalAlpha = alpha;
        traveller(n, s.nodes[0], (q * calls.length) % 1, cur.tone, alpha);
        statusLine(cur.txt, cur.tone);
      }

      if (name === 'checklist') {
        // The one thing all four of chapter 20's lists have in common: rows
        // going green before you commit to anything.
        const rows = ['HOME SCANNED', 'HS CURRENT', 'C3 CURRENT', 'K162s CHECKED', 'D-SCAN CLEAR', 'PROBES ABOARD'];
        const done = Math.min(rows.length, Math.floor(q * (rows.length + 1.6)));
        const listW = Math.min(boxW() * 0.86, 210 * k);
        const rowH = Math.min(22 * k, (boxH() - 26 * k) / rows.length);
        const x0 = px(0.5) - listW / 2;
        const y0 = boxTop() + (boxH() - rowH * rows.length) / 2;

        rows.forEach((row, i) => {
          const y = y0 + i * rowH;
          const on = i < done;
          const box = 10 * k;
          ctx.globalAlpha = alpha;
          roundRect(x0, y + (rowH - box) / 2, box, box, 2.5 * k);
          ctx.strokeStyle = on ? C.green : hexToRgba(C.cyan, 0.45);
          ctx.lineWidth = 1;
          if (on) {
            ctx.fillStyle = hexToRgba(C.green, 0.85);
            ctx.fill();
          }
          ctx.stroke();
          if (on) {
            // The tick, drawn rather than typed.
            ctx.strokeStyle = C.bg;
            ctx.lineWidth = 1.6 * k;
            ctx.lineCap = 'round';
            ctx.beginPath();
            ctx.moveTo(x0 + box * 0.24, y + rowH / 2);
            ctx.lineTo(x0 + box * 0.45, y + rowH / 2 + box * 0.22);
            ctx.lineTo(x0 + box * 0.78, y + rowH / 2 - box * 0.26);
            ctx.stroke();
            ctx.lineCap = 'butt';
          }
          ctx.font = `${8.5 * k}px ${FONT.mono}`;
          ctx.fillStyle = on ? hexToRgba(C.text, 0.55) : C.text;
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.fillText(row, x0 + box + 8 * k, y + rowH / 2);
          if (on) {
            // Struck through, the way a finished line reads on paper.
            ctx.strokeStyle = hexToRgba(C.green, 0.5);
            ctx.lineWidth = 1;
            const tw = ctx.measureText(row).width;
            ctx.beginPath();
            ctx.moveTo(x0 + box + 8 * k, y + rowH / 2);
            ctx.lineTo(x0 + box + 8 * k + tw, y + rowH / 2);
            ctx.stroke();
          }
        });

        const all = done >= rows.length;
        statusLine(all ? 'ALL CLEAR — GO' : `${done} / ${rows.length} CHECKED`, all ? C.green : hexToRgba(C.text, 0.75));
      }

      if (name === 'closing') {
        // Nothing to teach here. The chain simply breathes: each connection
        // lights in turn and fades, and the panel ends the document quietly.
        s.nodes.slice(1).forEach((n, i) => {
          const phase = (q * 2 + i / (s.nodes.length - 1)) % 1;
          const glowAmt = Math.max(0, Math.sin(phase * Math.PI));
          ctx.globalAlpha = alpha * (0.18 + glowAmt * 0.55);
          ctx.fillStyle = n.color;
          ctx.shadowColor = hexToRgba(n.color, 0.9);
          ctx.shadowBlur = 10 * glowAmt;
          ctx.beginPath();
          ctx.arc(px(n.x), py(n.y), (2 + glowAmt * 1.6) * k, 0, Math.PI * 2);
          ctx.fill();
          ctx.shadowBlur = 0;
        });
        ctx.globalAlpha = alpha;
        statusLine('SCAN THE CHAIN, NOT THE HOLE', hexToRgba(C.cyan, 0.65));
      }

      if (name === 'licence') {
        // Earned in order: each level fills, then unlocks the next.
        const lvl = Math.min(2, Math.floor(q * 3));
        const within = (q * 3) % 1;
        s.nodes.forEach((n, i) => {
          if (i > lvl) return;
          const full = i < lvl;
          const bw = nodeW(n);
          const bh = nodeH();
          const x = px(n.x) - bw / 2;
          const y = py(n.y) - bh / 2;
          ctx.save();
          ctx.globalAlpha = alpha * 0.85;
          ctx.beginPath();
          ctx.rect(x, y, bw * (full ? 1 : within), bh);
          ctx.clip();
          roundRect(x, y, bw, bh, 5 * k);
          ctx.fillStyle = hexToRgba(n.color, 0.3);
          ctx.fill();
          ctx.restore();
        });
        const pct = Math.round(((lvl + within) / 3) * 100);
        statusLine(
          `${['LEVEL 1 — EXPLORER', 'LEVEL 2 — WH PILOT', 'LEVEL 3 — OPERATOR'][lvl]}  ·  ${pct}%`,
          s.nodes[lvl].color,
        );
      }

      if (name === 'holecontrol') {
        // Observing the hole and reading what comes through it: 13.2's table
        // as four contacts, each arriving in turn with what it implies.
        const seen = [
          { i: 2, txt: 'SCANNER FRIGATE · BUILDING A MAP', tone: C.cyan },
          { i: 3, txt: 'COMBAT PROBES · HUNTING', tone: C.red },
          { i: 4, txt: 'ROLLING BS · STATIC ROLL', tone: C.gold },
          { i: 5, txt: 'SEVERAL PvP SHIPS · FLEET FORMING', tone: C.red },
        ];
        const upto = Math.min(seen.length, Math.floor(q * (seen.length + 1)));
        seen.forEach((c, i) => {
          if (i >= upto) return;
          // The contact's own line to the hole appears with it.
          drawLink(s.nodes[1], s.nodes[c.i], { from: 1, to: c.i, dashed: true }, alpha * 0.9);
          drawNode({ ...s.nodes[c.i], kind: 'wh', color: c.tone }, alpha, t);
          // Each contact reached us through the watched hole.
          traveller(s.nodes[c.i], s.nodes[1], (q * 2 + i / seen.length) % 1, c.tone, alpha * 0.55);
        });

        const mid = (px(s.nodes[0].x) + px(s.nodes[1].x)) / 2;
        label(`OBSERVED ${upto} / ${seen.length}`, mid, py(s.nodes[0].y) - 24 * k, hexToRgba(C.text, 0.7), 8.5, false);
        statusLine(
          upto === 0 ? 'WATCHING THE CONNECTION' : seen[upto - 1].txt,
          upto === 0 ? hexToRgba(C.text, 0.7) : seen[upto - 1].tone,
        );
      }

      if (name === 'homedefence') {
        const cx = px(s.nodes[1].x);
        const cy = py(s.nodes[1].y);
        // The hostile holding the hole, pulsing so it reads as present rather
        // than as another node.
        const pulse = 0.6 + 0.4 * Math.sin(t * 0.005);
        ctx.globalAlpha = alpha * pulse;
        ctx.strokeStyle = C.red;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(cx, cy, nodeClear(s.nodes[1]), 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = alpha;
        label('HOSTILE', cx, cy - nodeClear(s.nodes[1]) - 9 * k, C.red, 8, false);

        // Our ship leaving the site grid for a safe — never toward the hole.
        const safeX = px(0.5);
        const safeY = py(0.86);
        ctx.setLineDash([3, 3]);
        ctx.strokeStyle = hexToRgba(C.green, 0.5);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(safeX, safeY, 13 * k, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
        label('SAFE', safeX, safeY, hexToRgba(C.green, 0.9), 8, false);

        const run = Math.min(1, q / 0.5);
        const sx = px(s.nodes[0].x) + (safeX - px(s.nodes[0].x)) * run;
        const sy = py(s.nodes[0].y) + (safeY - py(s.nodes[0].y)) * run;
        ctx.fillStyle = C.green;
        ctx.shadowColor = hexToRgba(C.green, 0.9);
        ctx.shadowBlur = 8;
        ctx.beginPath();
        ctx.arc(sx, sy, 2.6 * k, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;

        statusLine(run < 1 ? 'BREAK OFF — GET SAFE' : 'SAFE · SCOUT THE CONNECTIONS', hexToRgba(C.green, 0.9));
      }

      if (name === 'route') {
        // The haul out to the hub, scout ahead.
        const legs = s.links.length;
        const idx = Math.floor(q * legs) % legs;
        const p = (q * legs) % 1;
        const l = s.links[idx];
        traveller(s.nodes[l.from], s.nodes[l.to], p, C.gold, alpha);
        const sl = s.links[(idx + 1) % legs];
        traveller(s.nodes[sl.from], s.nodes[sl.to], p, C.cyan, alpha * 0.7);
        statusLine('SCOUT AHEAD · FREIGHT BEHIND', hexToRgba(C.text, 0.7));
      }

      ctx.restore();
    };

    const paintScene = (name: HandbookScene, alpha: number, t: number, withStatus = true) => {
      labelling = withStatus;
      const s = SCENES[name];
      fitNodes(s);
      // A link to a node the overlay has not revealed yet is a line running
      // to nothing — it read as a stray artifact next to the diagram. The
      // overlay draws those itself, as each endpoint arrives.
      s.links.forEach((l) => {
        if (s.deferred?.includes(l.from) || s.deferred?.includes(l.to)) return;
        drawLink(s.nodes[l.from], s.nodes[l.to], l, alpha * 0.9);
      });
      s.nodes.forEach((n, i) => {
        if (s.deferred?.includes(i)) return;
        drawNode(n, alpha, t);
      });
      overlay(name, (t % PERIOD) / PERIOD, alpha, t);
      nodeScale = 1;
    };

    const draw = (t: number) => {
      // A hidden instance (the compact companion is `display: none` from lg
      // up, and stays mounted) measures 0x0, and every radius derived from
      // that goes negative. Nothing to draw there.
      //
      // But a zero measurement can also be stale: the panel's own column is
      // `display: none` until its breakpoint styles land, so the first
      // measure can read 0x0 for an element that is about to be 374px wide.
      // A ResizeObserver only reports box changes, so once w/h were latched
      // at 0 nothing forced a re-read and the canvas silently stopped
      // repainting — holding the previous chapter's scene forever while its
      // caption moved on. Re-measure before believing a zero.
      if (w <= 0 || h <= 0) {
        resize();
        if (w <= 0 || h <= 0) return;
      }
      ctx.clearRect(0, 0, w, h);
      // Exactly one scene, always. An earlier version clipped the outgoing
      // and incoming scenes to opposite sides of a moving sweep line; when
      // anything restarted that sweep faster than it could finish, the panel
      // sat at the start frame showing the *previous* chapter's diagram while
      // its caption named the current one. No composite, no failure mode —
      // the switch itself is an element-level dip (see the sx below).
      paintScene(sceneRef.current, 1, t);
    };

    /** One bad frame must not end the animation for the life of the page.
     * A negative-radius gradient threw inside `draw`, which killed the rAF
     * chain outright — the panel froze on its last good frame and went on
     * showing the previous chapter's scene while the caption beside it moved
     * on. Draw errors are logged once and the loop keeps its next frame. */
    let reportedDrawError = false;
    const loop = (t: number) => {
      try {
        draw(t);
      } catch (err) {
        if (!reportedDrawError) {
          reportedDrawError = true;
          console.error('ChainCanvas draw failed', err);
        }
      }
      frame = requestAnimationFrame(loop);
    };

    resize();
    const reduced = prefersReducedMotion();
    // One settled frame, mid-cycle, where each scene shows its point.
    const still = () => draw(PERIOD * 0.62);
    repaintRef.current = still;

    if (reduced) still();
    else frame = requestAnimationFrame(loop);

    const observer = new ResizeObserver(() => {
      resize();
      if (reduced) still();
    });
    observer.observe(canvas);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      repaintRef.current = null;
    };
  }, []);

  return (
    <Box
      component="canvas"
      ref={canvasRef}
      aria-hidden
      sx={{
        display: 'block',
        width: '100%',
        height,
        borderRadius: '8px',
        // The switch, expressed on the element rather than inside the canvas:
        // the readout dims and settles back as it retunes. It cannot show two
        // scenes, because the canvas only ever paints one.
        opacity: switching ? 0.25 : 1,
        transform: switching ? 'scale(0.985)' : 'none',
        transition: prefersReducedMotion()
          ? 'none'
          : 'opacity 0.23s ease-out, transform 0.23s cubic-bezier(0.16,1,0.3,1)',
      }}
    />
  );
};

export default ChainCanvas;
